import { d as debounce } from './index-DfRgBxEx.js';

/**
 * @class PredictiveSearch
 * @extends HTMLElement
 * @description Uses the Shopify predictive search api for rendering search results.
 * @link https://shopify.dev/api/ajax/reference/predictive-search
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults =
      this.querySelector('[data-predictive-search="results"]') ||
      document.querySelector('[data-predictive-search="results"]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.cachedResults = {};
    this.tabs = null;
    this.tabContents = null;
    this.predictiveSearchResults =
      this.querySelector('[data-predictive-search="results"]') ||
      document.querySelector('[data-predictive-search="results"]');
    this.activateTab = this.activateTab.bind(this);

    // Read the config from the attributes
    this.limit = this.getAttribute('limit');
    this.scope = this.getAttribute('scope');
    this.loading = false;
    this.resourceTypes = this.getAttribute('resource-types');
    this.searchFields = this.getAttribute('search-fields');

    // Elements and events
    this.input = this.querySelector('input[name="q"]');
    this.resetButton = this.querySelector('[data-predictive-search-reset]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');

    if (this.input) {
      this.input.addEventListener('focus', () => {
        this.setAttribute('input-active', true);
        this.setAttribute('input-open', true);
        const activeElement = this.querySelector('[data-predictive-search="active"]');
        activeElement?.setAttribute('aria-hidden', 'true');
      });
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 200).bind(this)
      );
    }

    this.input.form.addEventListener('submit', (event) => this.onFormSubmit(event));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', (event) => this.onKeyup(event));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
      this.submitButton.classList.add('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
      this.submitButton.classList.remove('hidden');
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.toggleResetButton();
    const newSearchTerm = this.getQuery();

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a'))
      event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();
    this.searchTerm = '';
    this.input.value = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.close();
    this.toggleResetButton();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close(true);
        this.removeAttribute('input-active');
        this.input.blur();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search="search-for-text"]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, a.button')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.setAttribute('loading', true);
    const loadingSkeleton = this.querySelector('[data-predictive-search="loading"]');
    loadingSkeleton?.setAttribute('aria-hidden', 'false');

    // Cancel if there is a pending request
    this.abortController.abort();
    this.abortController = new AbortController();

    const queryKey = searchTerm.replace(' ', '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.append('q', this.searchTerm);

    if (this.limit) searchParams.append('resources[limit]', this.limit);
    if (this.scope) searchParams.append('resources[limit_scope]', this.scope);

    const resourceTypes = this.resourceTypes || 'product';
    resourceTypes
      .split(',')
      .map((type) => type.trim())
      .filter(Boolean)
      .forEach((type) => searchParams.append('resources[type]', type));

    if (this.searchFields) {
      searchParams.append('resources[options][fields]', this.searchFields);
    }

    searchParams.append('section_id', 'predictive-search');

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        throw error;
      })
      .finally(() => {
        this.removeAttribute('loading');
        loadingSkeleton?.setAttribute('aria-hidden', 'true');
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('[data-predictive-search="status"]');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
        this.removeAttribute('input-active');
      }
    });
  }

  renderSearchResults(resultsMarkup) {
    if (!resultsMarkup) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.setAttribute('results', true);
    this.predictiveSearchResults.setAttribute('aria-hidden', 'false');
    this.setLiveRegionResults();
    this.open();

    this.tabs = this.querySelectorAll('[data-predictive-search-tab]');
    this.tabContents = this.querySelectorAll('[data-predictive-search-tab-content]');

    // Attach click events to tabs
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => this.activateTab(event.target));
    });

    // Activate the first tab by default
    if (this.tabs.length > 0) {
      this.activateTab(this.tabs[0].querySelector('button'));
    }
  }

  activateTab(activeTab) {
    // Ensure the activeTab is the correct parent element
    const correctTab = activeTab.closest('[data-predictive-search-tab]');

    if (!correctTab) {
      console.warn('No valid tab element found for activation.');
      return;
    }

    // Clear active states from all tabs and hide all content
    this.tabs.forEach((tab) =>
      tab.querySelector('button').classList.remove('bg-white', 'text-[--primary-link-search-color]')
    );
    this.tabContents.forEach((content) => content.classList.add('hidden'));

    correctTab
      .querySelector('button')
      .classList.add('bg-white', 'text-[--primary-link-search-color]');

    const tabName = correctTab.getAttribute('data-predictive-search-tab');

    const contentSelector = `[data-predictive-search-tab-content="${tabName}"]`;

    const targetContent =
      this.querySelector(contentSelector) || document.querySelector(contentSelector);

    if (!targetContent) {
      return;
    }

    // Show the corresponding content
    targetContent.classList.remove('hidden');
    targetContent.classList.add('grid');
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]').textContent
    );
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.setAttribute('aria-hidden', 'true');
  }
}

customElements.define('predictive-search', PredictiveSearch);
